import api from './api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PaymentAttempt {
  id: number
  amount: string
  payment_method: string
  provider: string
  status: 'initiated' | 'processing' | 'completed' | 'failed' | 'refunded' | 'cancelled'
  provider_transaction_id: string
  error_message: string
  error_code: string
  rejection_reason: string
  status_message: string
  payment_url: string
  installment_count: number
  payment_date: string | null
  created_at: string
}

export interface InvoiceDetail {
  id: number
  invoice_number: string
  type: string
  amount: string
  paid_amount: string | null
  currency_code: string
  status: 'pending' | 'partially_paid' | 'paid' | 'receipt_uploaded' | 'rejected' | 'overdue' | 'refunded' | 'cancelled'
  due_date: string | null
  description: string
  rejection_reason: string
  status_message: string
  order_id?: string
  created_at: string
  updated_at: string
  payments: PaymentAttempt[]
}

export interface BillingInfo {
  name?: string
  phone?: string
  address?: string
  city?: string
  district?: string
}

export interface PaymentInitiateRequest {
  provider: 'denizbank' | 'netahsilat' | 'merchantsafepay' | 'bank_transfer'
  installment_count?: number
  currency_code?: string
  billing?: BillingInfo
}

/** Denizbank / Netahsilat: browser renders a hidden form and auto-POSTs it. */
export interface FormPostResponse {
  type: 'form_post'
  action_url: string
  fields: Record<string, string>
  open_in_same_window: boolean
}

/** MerchantSafePay: browser is redirected to the payment page URL. */
export interface RedirectResponse {
  type: 'redirect'
  payment_url: string
}

/** Bank transfer: member must upload a receipt separately. */
export interface BankTransferResponse {
  type: 'bank_transfer'
  payment_id: number
  upload_url: string
}

export type PaymentInitiateResponse = FormPostResponse | RedirectResponse | BankTransferResponse

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export type PaymentProvider = 'denizbank' | 'netahsilat' | 'merchantsafepay' | 'bank_transfer'

/** A single active payment option returned from the rules engine. */
export interface PaymentOption {
  id: number
  name: string
  /** Identifies the flow: 'bank_transfer' → receipt upload; anything else → online gateway */
  result: string
  sort_order: number
}

/**
 * Helper to determine if a payment option is bank transfer.
 * Uses name field since result can be empty in Salesforce.
 */
export function isBankTransferOption(option: PaymentOption | null | undefined): boolean {
  if (!option) return false
  const name = option.name.toLowerCase()
  return name.includes('bank') && name.includes('transfer')
}

export interface InvoiceByApplicationResponse extends InvoiceDetail {
  available_providers: PaymentProvider[]
}

/**
 * Fetch the invoice for an application by application number.
 * Also returns the list of active payment providers for the institution.
 */
export async function getInvoiceByApplication(appNumber: string): Promise<InvoiceByApplicationResponse> {
  const response = await api.get<InvoiceByApplicationResponse>(
    `/v1/payments/invoices/by-application/${appNumber}/`,
  )
  return response.data
}

/**
 * Fetch invoice details and all payment attempts for the authenticated member.
 */
export async function getInvoice(invoiceId: number): Promise<InvoiceDetail> {
  const response = await api.get<InvoiceDetail>(`/v1/payments/invoices/${invoiceId}/`)
  return response.data
}

/**
 * Initiate a payment for an invoice.
 *
 * Returns a provider-specific payload:
 *   - denizbank / netahsilat → FormPostResponse (hidden form fields)
 *   - merchantsafepay        → RedirectResponse (payment_url)
 *   - bank_transfer          → BankTransferResponse (upload_url)
 */
export async function initiatePayment(
  invoiceId: number,
  payload: PaymentInitiateRequest,
): Promise<PaymentInitiateResponse> {
  const response = await api.post<PaymentInitiateResponse>(
    `/v1/payments/invoices/${invoiceId}/initiate/`,
    payload,
  )
  return response.data
}

/**
 * Upload a bank transfer receipt (PDF, JPEG, or PNG, max 10 MB).
 */
export async function uploadBankTransferReceipt(
  invoiceId: number,
  file: File,
  onUploadProgress?: (percent: number) => void,
): Promise<{ payment_id: number; document_id: number }> {
  const formData = new FormData()
  formData.append('receipt', file)

  const response = await api.post<{ payment_id: number; document_id: number }>(
    `/v1/payments/invoices/${invoiceId}/bank-transfer/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (event) => {
            const percent = event.total
              ? Math.round((event.loaded * 100) / event.total)
              : 0
            onUploadProgress(percent)
          }
        : undefined,
    },
  )
  return response.data
}

/**
 * Fetch active payment_option requirements for the current institution,
 * optionally filtered by criteria evaluated against the application's invoice.
 *
 * Pass ``applicationNumber`` so the backend can resolve the linked invoice and
 * hide payment options whose criteria do not match.
 */
export async function getPaymentOptions(applicationNumber?: string): Promise<PaymentOption[]> {
  const params = applicationNumber ? { application_number: applicationNumber } : undefined
  const response = await api.get<PaymentOption[]>('/v1/payments/payment-options/', { params })
  return response.data
}
